import type { FlowRunTargets, MultiRobotConnectionManager } from './MultiRobotConnectionManager';
import { RpcDisconnectedError, RpcRemoteError, RpcTimeoutError, type RpcCallOptions, type RpcService } from './rpcTypes';
import { WEGO_JOINTS } from '@/flow/animation/wegoJoints';
import type { CustomMotionConfig } from '@/flow/config/customMotions';
import { playSynchronizedSpeech, SharedSpeechCache } from './synchronizedSpeech';

export type RobotCommandResult<T = unknown> = {
  robotId: string;
  status: 'fulfilled' | 'rejected' | 'timeout' | 'disconnected';
  value?: T;
  errorCode?: string;
  message?: string;
  durationMs: number;
};

export type BatchCommandSummary<T = unknown> = {
  results: RobotCommandResult<T>[];
  activeRobotIdsAfter: string[];
};

export type FlowCommandOptions = {
  nodeId: string; signal?: AbortSignal; timeoutMs?: number; speechAudio?: ArrayBuffer;
  introductionMotionId?: number;
  onSpeechStarted?: () => void;
  onSpeechPartCompleted?: (part: 'audio' | 'motion') => void;
};

export const FLOW_EFFECT_RPC_TIMEOUT_MS = 3_000;
export const FLOW_CUSTOM_GESTURE_RPC_TIMEOUT_MS = 10_000;
export const FLOW_TOF_RPC_TIMEOUT_MS = 3_500;

export type BatchRobotManager = {
  synthesizeSpeech: MultiRobotConnectionManager['synthesizeSpeech'];
  getRunTargetSession: (targets: FlowRunTargets, robotId: string) => {
    call<T = unknown>(service: RpcService, data?: unknown, options?: RpcCallOptions): Promise<T>;
  } | undefined;
};

const LED_OFF = { red: 0, green: 0, blue: 0, mode: 0 } as const;

const toPhysicalMotorJoints = (joints: Record<string, number>) => Object.fromEntries(
  Object.entries(joints).flatMap(([jointId, degrees]) => {
    const config = WEGO_JOINTS[jointId as keyof typeof WEGO_JOINTS];
    if (!config?.isMotor || !Number.isFinite(degrees)) return [];
    return [[jointId, Math.min(config.max, Math.max(config.min, degrees))]];
  }),
);

export const toCustomGestureRpcPayload = (motion: CustomMotionConfig) => ({
  id: motion.id,
  label: motion.label,
  poses: motion.poses.map((pose) => ({
    timeMs: pose.timeMs,
    joints: toPhysicalMotorJoints(pose.joints),
    ...(pose.ledColor ? { ledColor: pose.ledColor } : {}),
    ...(pose.sound ? { sound: pose.sound } : {}),
  })),
});

type LedCommand = {
  red: number;
  green: number;
  blue: number;
  mode: 0 | 1;
};

export class BatchRobotService {
  private readonly excludedRobotIds = new Set<string>();
  private readonly ledOnTargetRobotIds = new Set<string>();
  private readonly speechCache = new SharedSpeechCache();
  private activeSpeech: AbortController | null = null;
  private speechCompletion: Promise<void> = Promise.resolve();

  constructor(private readonly manager: BatchRobotManager, readonly targets: FlowRunTargets) {}

  synthesizeSpeech = (data: Parameters<MultiRobotConnectionManager['synthesizeSpeech']>[0], signal?: AbortSignal) => (
    this.manager.synthesizeSpeech(data, signal)
  );

  async speak(
    data: { text: string; speaker?: number; speedScale?: number; pitchScale?: number },
    options: FlowCommandOptions,
  ) {
    if (this.targets.robotIds.length < 2 && !options.speechAudio) {
      return this.callMany('speak', { ...data, cacheable: true }, options);
    }
    const robotIds = [...new Set(this.targets.robotIds)].filter((robotId) => (
      !this.excludedRobotIds.has(robotId) && this.manager.getRunTargetSession(this.targets, robotId)
    ));
    if (robotIds.length === 0) return { results: [], activeRobotIdsAfter: [] };
    this.activeSpeech?.abort();
    const previousSpeech = this.speechCompletion;
    let completeSpeech!: () => void;
    this.speechCompletion = new Promise<void>((resolve) => { completeSpeech = resolve; });
    const controller = new AbortController();
    this.activeSpeech = controller;
    const abort = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', abort, { once: true });
    if (options.signal?.aborted) abort();
    const startedAt = performance.now();
    try {
      // Finish the cancelled transfer's stop acknowledgements before starting a replacement.
      await previousSpeech;
      controller.signal.throwIfAborted();
      const audio = options.speechAudio ?? await this.speechCache.get(data, this.manager.synthesizeSpeech, controller.signal);
      await playSynchronizedSpeech(robotIds.map((robotId) => ({
        call: <T = unknown>(service: RpcService, payload?: unknown, callOptions?: RpcCallOptions) => {
          const session = this.manager.getRunTargetSession(this.targets, robotId);
          if (!session) return Promise.reject(new RpcDisconnectedError(robotId));
          return session.call<T>(service, payload, callOptions);
        },
      })), audio, {
        nodeId: options.nodeId, timeoutMs: options.timeoutMs,
        flowRunId: this.targets.runId, signal: controller.signal,
      }, options.introductionMotionId !== undefined || options.onSpeechStarted || options.onSpeechPartCompleted ? {
        ...(options.introductionMotionId !== undefined ? { motionId: options.introductionMotionId } : {}),
        onStarted: options.onSpeechStarted,
        onCompleted: options.onSpeechPartCompleted,
      } : undefined);
      return {
        results: robotIds.map((robotId): RobotCommandResult => ({
          robotId, status: 'fulfilled', value: true, durationMs: performance.now() - startedAt,
        })),
        activeRobotIdsAfter: robotIds,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return {
        results: robotIds.map((robotId): RobotCommandResult => ({
          robotId,
          status: error instanceof RpcTimeoutError ? 'timeout' : 'rejected',
          message,
          durationMs: performance.now() - startedAt,
        })),
        activeRobotIdsAfter: robotIds.filter((robotId) => this.manager.getRunTargetSession(this.targets, robotId)),
      };
    } finally {
      options.signal?.removeEventListener('abort', abort);
      if (this.activeSpeech === controller) this.activeSpeech = null;
      completeSpeech();
    }
  }

  startMotion(motionNo: number, options: FlowCommandOptions) {
    return this.callMany('startMotion', motionNo, options);
  }

  walkForwardSteps(steps: number, options: FlowCommandOptions) {
    return this.callMany('walkForwardSteps', { steps }, options);
  }

  walkBackwardSteps(steps: number, options: FlowCommandOptions) {
    return this.callMany('walkBackwardSteps', { steps }, options);
  }

  walkLeftSteps(steps: number, options: FlowCommandOptions) {
    return this.callMany('walkLeftSteps', { steps }, options);
  }

  walkRightSteps(steps: number, options: FlowCommandOptions) {
    return this.callMany('walkRightSteps', { steps }, options);
  }

  walkCurveRightSteps(steps: number, options: FlowCommandOptions) {
    return this.callMany('walkCurveRightSteps', { steps }, options);
  }

  walkCurveLeftSteps(steps: number, options: FlowCommandOptions) {
    return this.callMany('walkCurveLeftSteps', { steps }, options);
  }

  playCustomGesture(motion: CustomMotionConfig, options: FlowCommandOptions) {
    return this.callMany('playCustomGesture', toCustomGestureRpcPayload(motion), {
      ...options,
      timeoutMs: options.timeoutMs ?? FLOW_CUSTOM_GESTURE_RPC_TIMEOUT_MS,
    });
  }

  getCurrentAngles(vids: number[], options: FlowCommandOptions) {
    return this.callMany<Record<string, number | null>>('getCurrentAngles', vids, {
      ...options,
      timeoutMs: options.timeoutMs ?? FLOW_EFFECT_RPC_TIMEOUT_MS,
    });
  }

  measureTOF(data: { mode: 0 | 1 | 2; area: number }, options: FlowCommandOptions) {
    return this.callMany<number>('measureTOF', data, {
      ...options,
      timeoutMs: options.timeoutMs ?? FLOW_TOF_RPC_TIMEOUT_MS,
    });
  }

  playSound(
    data: { sound: 'wake' | 'found' | 'listen' | 'thinking' },
    options: FlowCommandOptions,
  ) {
    return this.callMany('playSound', data, {
      ...options,
      timeoutMs: options.timeoutMs ?? FLOW_EFFECT_RPC_TIMEOUT_MS,
    });
  }

  setLed(data: LedCommand, options: FlowCommandOptions) {
    return this.callMany(
      'setLed',
      data,
      {
        ...options,
        timeoutMs: options.timeoutMs ?? FLOW_EFFECT_RPC_TIMEOUT_MS,
      },
      this.targets.robotIds,
      data.mode === 1
        ? (robotId) => { this.ledOnTargetRobotIds.add(robotId); }
        : undefined,
    );
  }

  cleanupLed(options: Pick<FlowCommandOptions, 'nodeId' | 'timeoutMs'>) {
    return this.callMany(
      'setLed',
      LED_OFF,
      {
        nodeId: options.nodeId,
        timeoutMs: options.timeoutMs ?? FLOW_EFFECT_RPC_TIMEOUT_MS,
      },
      [...this.ledOnTargetRobotIds],
    );
  }

  async stopSpeak(options: FlowCommandOptions) {
    this.activeSpeech?.abort();
    await this.speechCompletion;
    return this.callMany('stopSpeak', undefined, options);
  }

  hasActiveTargets() {
    return this.targets.robotIds.some((robotId) => (
      !this.excludedRobotIds.has(robotId)
      && this.manager.getRunTargetSession(this.targets, robotId) !== undefined
    ));
  }

  private async callMany<T>(
    service: RpcService,
    data: unknown,
    options: FlowCommandOptions,
    candidateRobotIds: readonly string[] = this.targets.robotIds,
    beforeSend?: (robotId: string) => void,
  ): Promise<BatchCommandSummary<T>> {
    const active = [...new Set(candidateRobotIds)].flatMap((robotId) => {
      if (this.excludedRobotIds.has(robotId)) return [];
      const session = this.manager.getRunTargetSession(this.targets, robotId);
      return session ? [{ robotId, session }] : [];
    });
    const results = await Promise.all(active.map(({ robotId, session }) => {
      beforeSend?.(robotId);
      const callOptions: RpcCallOptions = {
        flowRunId: this.targets.runId,
        nodeId: options.nodeId,
        signal: options.signal,
        timeoutMs: options.timeoutMs,
      };
      const startedAt = performance.now();
      return session.call<T>(service, data, callOptions)
        .then((value): RobotCommandResult<T> => ({ robotId, status: 'fulfilled', value, durationMs: performance.now() - startedAt }))
        .catch((error): RobotCommandResult<T> => {
          const durationMs = performance.now() - startedAt;
          if (error instanceof RpcTimeoutError) return { robotId, status: 'timeout', message: error.message, durationMs };
          if (error instanceof RpcDisconnectedError) return { robotId, status: 'disconnected', message: error.message, durationMs };
          if (error instanceof RpcRemoteError) return { robotId, status: 'rejected', errorCode: error.payload.code, message: error.message, durationMs };
          return { robotId, status: 'rejected', message: error instanceof Error ? error.message : String(error), durationMs };
        });
    }));
    results.filter((result) => result.status === 'disconnected').forEach((result) => {
      this.excludedRobotIds.add(result.robotId);
    });
    return {
      results,
      activeRobotIdsAfter: this.targets.robotIds.filter((robotId) => (
        !this.excludedRobotIds.has(robotId)
        && this.manager.getRunTargetSession(this.targets, robotId) !== undefined
      )),
    };
  }
}
